import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { CalendarBlank, CaretLeft, CaretRight, CheckCircle, Warning } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const pad = (n) => String(n).padStart(2, "0");
const fmtDay = (d) => new Date(`${d}T12:00:00`).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });

export default function PublicScheduleSecondary() {
  const { pid, token } = useParams();
  const now = new Date();
  const [ym, setYm] = useState({ year: now.getFullYear(), month: now.getMonth() + 1 });
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState(null);
  const [booking, setBooking] = useState(false);
  const [done, setDone] = useState(null);
  const [bookError, setBookError] = useState("");

  const load = () => {
    setLoading(true);
    setPicked(null);
    axios.get(`${API}/practices/${pid}/schedule-secondary/${token}`, { params: ym })
      .then((r) => { setInfo(r.data); setError(""); })
      .catch((e) => setError(e.response?.data?.detail || "Link non valido"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [pid, token, ym.year, ym.month]); // eslint-disable-line react-hooks/exhaustive-deps

  const byDay = useMemo(() => {
    const g = {};
    (info?.slots || []).forEach((s) => { (g[s.date] = g[s.date] || []).push(s); });
    return g;
  }, [info]);

  const shiftMonth = (d) => {
    const m = ym.month + d;
    setYm({ year: ym.year + Math.floor((m - 1) / 12), month: ((m - 1 + 12) % 12) + 1 });
  };
  const isCurrentMonth = ym.year === now.getFullYear() && ym.month === now.getMonth() + 1;

  const book = async () => {
    setBooking(true);
    setBookError("");
    try {
      const r = await axios.post(`${API}/practices/${pid}/book-secondary-slot/${token}`, picked);
      setDone(r.data);
    } catch (e) {
      const msg = e.response?.data?.detail || "Prenotazione non riuscita";
      if ([404, 410].includes(e.response?.status)) {
        setInfo(null);
        setError(msg);
      } else {
        setBookError(msg);
        load();
      }
    } finally {
      setBooking(false);
    }
  };

  const Shell = ({ children }) => (
    <div className="min-h-screen bg-slate-50 flex items-start justify-center px-4 pt-12 pb-40" data-testid="schedule-secondary-page">
      <div className="w-full max-w-xl bg-white rounded-2xl shadow-sm border p-6 space-y-5">{children}</div>
    </div>
  );

  if (done) {
    return (
      <Shell>
        <div className="text-center space-y-3 py-6" data-testid="schedule-secondary-done">
          <CheckCircle size={48} weight="fill" className="text-emerald-600 mx-auto" />
          <h1 className="text-xl font-semibold text-slate-900">Sopralluogo prenotato</h1>
          <p className="text-sm text-slate-600">
            {picked && `${fmtDay(picked.date)} alle ${pad(picked.hour)}:${pad(picked.minute)}`}. Riceverai eventuali aggiornamenti via email.
          </p>
        </div>
      </Shell>
    );
  }

  if (error && !info) {
    return (
      <Shell>
        <div className="text-center space-y-3 py-6" data-testid="schedule-secondary-error">
          <Warning size={40} className="text-amber-500 mx-auto" />
          <p className="text-slate-700">{error}</p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div>
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-blue-700">
          <CalendarBlank size={14} weight="bold" /> Prenota il sopralluogo
        </div>
        <h1 className="text-2xl font-semibold text-slate-900 mt-1" data-testid="schedule-secondary-service">{info?.service_name}</h1>
        {info?.address && <p className="text-sm text-slate-500">{info.address}</p>}
        <p className="text-xs text-slate-500 mt-1">Durata prevista: {info?.duration_minutes} minuti</p>
      </div>

      <div className="flex items-center justify-between">
        <Button variant="outline" size="sm" onClick={() => shiftMonth(-1)} disabled={isCurrentMonth} data-testid="schedule-prev-month">
          <CaretLeft size={14} />
        </Button>
        <div className="text-sm font-medium capitalize">
          {new Date(ym.year, ym.month - 1, 1).toLocaleDateString("it-IT", { month: "long", year: "numeric" })}
        </div>
        <Button variant="outline" size="sm" onClick={() => shiftMonth(1)} data-testid="schedule-next-month">
          <CaretRight size={14} />
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Caricamento disponibilità…</div>
      ) : Object.keys(byDay).length === 0 ? (
        <div className="text-sm text-slate-500" data-testid="schedule-no-slots">Nessuna disponibilità in questo mese: prova il mese successivo.</div>
      ) : (
        <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1" data-testid="schedule-slots">
          {Object.keys(byDay).sort().map((d) => (
            <div key={d}>
              <div className="text-xs font-semibold text-slate-700 capitalize mb-1.5">{fmtDay(d)}</div>
              <div className="flex flex-wrap gap-1.5">
                {byDay[d].map((s) => {
                  const k = `${s.date}_${s.hour}_${s.minute}`;
                  const active = picked && `${picked.date}_${picked.hour}_${picked.minute}` === k;
                  return (
                    <button
                      key={k}
                      type="button"
                      onClick={() => setPicked(s)}
                      className={`px-2.5 py-1 rounded-md border text-sm transition-colors ${active ? "bg-blue-600 border-blue-600 text-white" : "bg-white border-slate-200 hover:border-blue-400"}`}
                      data-testid={`schedule-slot-${k}`}
                    >
                      {pad(s.hour)}:{pad(s.minute)}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {bookError && (
        <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800" data-testid="schedule-book-error">
          {bookError}
        </div>
      )}
      <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white" disabled={!picked || booking} onClick={book} data-testid="schedule-confirm-btn">
        {booking ? "Prenotazione…" : picked ? `Conferma ${fmtDay(picked.date)} ore ${pad(picked.hour)}:${pad(picked.minute)}` : "Seleziona uno slot"}
      </Button>
    </Shell>
  );
}
